import { type DietaryRestrictionsText } from '@hungryroot/dietary-restrictions';
import { usePairingInventoryParams } from '@hungryroot/inventory';
import { useProductList } from '@hungryroot/rdp';
import {
  type PairingDetail,
  type PairingV1 as Pairing,
} from '@hungryroot/rest';
import { useCustomerServingSize } from '../../../../hooks/pricing_config/get/get-customer-pricing-config';
import { type PairingWithIngredientDetails } from '../../../../hooks/useEditRecipe/useEditRecipe';
import { getShowServingSizeWarningModal } from '../../../ServingSizeWarningModal/getShowServingSizeWarningModal';
import { getWarning } from '../utils/getWarning';
import { useProductInfo } from '../utils/useProductInfo';

interface UseRDPModalViewModelProps {
  currentPairing: PairingWithIngredientDetails;
  pairingV2: PairingDetail | undefined;
  dietaryIngredients: DietaryRestrictionsText[];
  ingredientContainsNeverPeriodicity: boolean;
  orderId?: number;
  initialPairing: Pairing;
}

export const useRDPModalViewModel = ({
  currentPairing,
  pairingV2,
  dietaryIngredients,
  ingredientContainsNeverPeriodicity,
  orderId,
  initialPairing,
}: UseRDPModalViewModelProps) => {
  const customerPlanServingSize = useCustomerServingSize();
  const showServingSizeWarningModal = getShowServingSizeWarningModal(
    currentPairing.servings,
    customerPlanServingSize
  );
  const outOfStockParams = usePairingInventoryParams(orderId);
  const { data: fullIngredientsList } = useProductList({
    productIds: initialPairing.ingredients.map((i) => i.id),
    ...outOfStockParams,
  });

  // A product is only out of stock if the product is_merchandized field is explicitly false
  // In order for this recipe to not be shown as out of stock, no ingredient can be explicitly marked as out of stock
  const areAllIngredientsInStock = fullIngredientsList?.every(
    (ingredient) => ingredient.is_merchandized !== false
  );

  const warning = getWarning(
    dietaryIngredients,
    ingredientContainsNeverPeriodicity
  );

  const productInfo = useProductInfo({
    currentPairing,
    pairingV2,
  });

  return {
    customerPlanServingSize,
    showServingSizeWarningModal,
    areAllIngredientsInStock,
    warning,
    productInfo,
  };
};
